import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getGratitudeEntries, getGratitudeToday, saveGratitude } from '../lib/api';
import { IMAGES, SKETCH_PATTERNS } from '../lib/images';
import { Loader2, Plus, Heart, Calendar, X } from 'lucide-react';

export default function GratitudePage() {
  const { deviceId } = useAuth();
  const [entries, setEntries] = useState([]);
  const [todayEntries, setTodayEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      const [allRes, todayRes] = await Promise.allSettled([
        getGratitudeEntries(deviceId),
        getGratitudeToday(deviceId),
      ]);
      if (allRes.status === 'fulfilled') setEntries(allRes.value.data?.entries || allRes.value.data || []);
      if (todayRes.status === 'fulfilled') setTodayEntries(todayRes.value.data?.entries || todayRes.value.data || []);
    } catch {}
    setLoading(false);
  }, [deviceId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleSave = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await saveGratitude({ device_id: deviceId, content: content.trim() });
      setContent('');
      setShowForm(false);
      await loadEntries();
    } catch {}
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="gratitude-page">
      <div className="flex items-start sm:items-center justify-between gap-4 mb-8 flex-col sm:flex-row">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Gratitude Journal</h1>
          <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>Reflect on the things that bring you joy</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-gratitude-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all hover:opacity-90 active:scale-[0.98] shrink-0"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
          <Plus className="w-4 h-4" /> Add Entry
        </button>
      </div>

      {/* Hero Section with watercolor illustration */}
      <div className="rounded-lg overflow-hidden mb-8 relative" style={{ height: '240px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.gratitude})` }} />
        {/* Subtle bottom gradient only, for text legibility */}
        <div className="absolute inset-x-0 bottom-0 h-1/2"
          style={{ background: 'linear-gradient(to top, rgba(44,74,59,0.78) 0%, rgba(44,74,59,0.4) 50%, rgba(0,0,0,0) 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 p-6 text-left">
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-5 h-5 text-white/90" />
            <p className="text-xs text-white/80 uppercase tracking-widest font-semibold" style={{ fontFamily: 'Manrope' }}>Gratitude</p>
          </div>
          <p className="text-white text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
            {todayEntries.length > 0
              ? `${todayEntries.length} note${todayEntries.length > 1 ? 's' : ''} today — keep it going`
              : 'What are you grateful for today?'}
          </p>
        </div>
      </div>

      {/* New Entry Form */}
      {showForm && (
        <div className="rounded-lg p-6 border mb-6 animate-fade-in" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="gratitude-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>New Gratitude Entry</h3>
            <button onClick={() => setShowForm(false)} style={{ color: 'var(--muted-fg)' }}><X className="w-5 h-5" /></button>
          </div>
          <textarea data-testid="gratitude-input" value={content} onChange={e => setContent(e.target.value)}
            className="w-full p-4 rounded-lg outline-none resize-none h-32"
            style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
            placeholder="I am grateful for..." />
          <div className="flex justify-end mt-3">
            <button onClick={handleSave} disabled={saving || !content.trim()} data-testid="save-gratitude-btn"
              className="px-6 py-2 rounded-lg font-medium text-sm transition-all hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="space-y-4">
        {(Array.isArray(entries) ? entries : []).length === 0 && !showForm && (
          <div className="text-center py-16">
            <Heart className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--muted)' }} />
            <p className="font-medium" style={{ color: 'var(--text)' }}>No entries yet</p>
            <p className="text-sm" style={{ color: 'var(--muted-fg)' }}>Start your gratitude practice by adding your first entry</p>
          </div>
        )}
        {(Array.isArray(entries) ? entries : []).length > 0 && (
          <h2 className="text-lg sm:text-xl font-bold pt-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Past reflections
          </h2>
        )}
        {(Array.isArray(entries) ? entries : []).map((entry, i) => {
          const d = entry.date ? new Date(entry.date) : null;
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
          let relative = '';
          if (d) {
            const dStart = new Date(d); dStart.setHours(0, 0, 0, 0);
            if (dStart.getTime() === today.getTime()) relative = 'Today';
            else if (dStart.getTime() === yesterday.getTime()) relative = 'Yesterday';
          }
          const weekday = d ? d.toLocaleDateString('en-US', { weekday: 'long' }) : '';
          const fullDate = d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Recent';
          return (
            <article key={i}
              className={`rounded-xl border overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md animate-fade-in stagger-${(i % 6) + 1}`}
              style={{ background: '#FEFBF3', borderColor: '#E8E0CF' }}
              data-testid={`gratitude-entry-${i}`}>
              {/* Date header with accent rail on the left */}
              <header className="flex items-center gap-3 px-5 sm:px-7 pt-5 pb-2 border-b"
                style={{ borderColor: '#F1EADD' }}>
                <div className="w-1 h-10 rounded-full shrink-0" style={{ background: '#C3A87C' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-[15px] font-bold leading-tight"
                    style={{ fontFamily: 'Manrope', color: '#3B3020' }}>
                    {relative || weekday}
                  </p>
                  <p className="text-xs flex items-center gap-1.5 mt-0.5"
                    style={{ color: '#8B7355', fontFamily: 'Manrope' }}>
                    <Calendar className="w-3 h-3" /> {fullDate}
                  </p>
                </div>
                {entry.entry_count > 1 && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold shrink-0"
                    style={{ background: '#C3A87C20', color: '#8B7355' }}>
                    {entry.entry_count} notes
                  </span>
                )}
              </header>
              {/* Entry body with journal-style indent + larger readable type */}
              <div className="px-5 sm:px-7 py-5">
                <p className="text-base sm:text-[17px] leading-[1.75] whitespace-pre-wrap break-words"
                  style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    color: '#2A2218',
                  }}>
                  {entry.content}
                </p>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
